import { useEffect, useState } from 'react';
import { Bond, BondPayload, mensagemDeErro } from '../api';
import { useAtualizarVinculo, useCriarVinculo } from '../hooks';
import { BondFields } from './BondFields';
import { cleanBondPayload, validateBondForm } from './format';
import { PessoasModal } from './PessoasModal';

interface Props {
  open: boolean;
  personId: string;
  /** Com vínculo: edita o contrato; sem: cria novo vínculo (recontratação). */
  bond: Bond | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}

export function BondFormModal({ open, personId, bond, onClose, onSaved }: Props) {
  const criar = useCriarVinculo(personId);
  const atualizar = useAtualizarVinculo();
  const [value, setValue] = useState<Partial<BondPayload>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setSubmitError(null);
    setValue(
      bond
        ? {
          companyId: bond.companyId,
          employmentType: bond.employmentType,
          admissionDate: bond.admissionDate,
          contractKind: bond.contractKind,
          contractEndDate: bond.contractEndDate ?? undefined,
          probationEndDate: bond.probationEndDate ?? undefined,
          probationExtensionEndDate: bond.probationExtensionEndDate ?? undefined,
        }
        : { employmentType: 'clt', contractKind: 'indeterminado' },
    );
  }, [open, bond]);

  async function submit() {
    const found = validateBondForm(value);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    const payload = cleanBondPayload(value as BondPayload);
    try {
      if (bond) {
        await atualizar.mutateAsync({
          id: bond.id,
          payload: {
            admissionDate: payload.admissionDate,
            contractKind: payload.contractKind,
            // `null` limpa a data que deixou de se aplicar ao contrato.
            contractEndDate: payload.contractEndDate ?? (null as unknown as undefined),
            probationEndDate: payload.probationEndDate ?? (null as unknown as undefined),
            probationExtensionEndDate: payload.probationExtensionEndDate ?? (null as unknown as undefined),
          },
        });
        onSaved('Contrato do vínculo atualizado.');
      } else {
        const created = await criar.mutateAsync(payload);
        onSaved(`Vínculo criado com a matrícula ${created.internalRegistration}.`);
      }
    } catch (err) {
      setSubmitError(mensagemDeErro(err, 'Não foi possível salvar o vínculo.'));
    }
  }

  return (
    <PessoasModal
      open={open}
      size="lg"
      title={bond ? 'Editar contrato do vínculo' : 'Novo vínculo'}
      description={bond ? `Matrícula ${bond.internalRegistration}` : 'Os dados pessoais vigentes são reaproveitados.'}
      submitLabel={bond ? 'Salvar' : 'Criar vínculo'}
      isSubmitting={criar.isPending || atualizar.isPending}
      submitError={submitError}
      onClose={onClose}
      onSubmit={submit}
    >
      <BondFields value={value} onChange={(patch) => setValue((v) => ({ ...v, ...patch }))} errors={errors} editing={!!bond} />
    </PessoasModal>
  );
}
